"use client";

import { TrialDto } from "@decibeltrade/sdk";
import dynamic from "next/dynamic";
import { css } from "styled-system/css";
import { flex, stack } from "styled-system/patterns";

import { DetailsEmptyTrades } from "./details-empty-trades";
import { DetailsHeader, DetailsHeaderProps } from "./details-header";
import { DetailsHero } from "./details-hero";
import { DetailsMetricsRow, DetailsMetricsRowProps } from "./details-metrics-row";
import { ClaimState, FundsUnlockFooter } from "./funds-unlock-footer";
import { SettleScreen } from "./settle-screen";
import {
  SettleAction,
  TrialFootnote,
  TrialHistorySection,
  TrialPositionsTable,
  TrialPositionsTableSkeleton,
} from "./trial-positions-table";

const FftTrialChartDynamic = dynamic(
  () => import("./fft-trial-chart").then((m) => m.FftTrialChart),
  { ssr: false },
);

export function preloadFftTrialChart() {
  void import("./fft-trial-chart");
}

export type DetailsBodyVariant = "pre-lock" | "post-lock-empty" | "active-trial" | "settled-trial";

export interface DetailsPaneProps {
  header: DetailsHeaderProps;
  metrics: DetailsMetricsRowProps["metrics"];
  bodyVariant: DetailsBodyVariant;
  trialMinutes: number;
  /** Market address for eager chart rendering before trial data arrives. */
  marketAddr?: string;
  account?: string;
  activeTrial?: TrialDto;
  lockCountdown?: string | null;
  claim?: ClaimState;
  settle?: SettleAction;
  positionFlat?: boolean;
  settleStalled?: boolean;
}

export function DetailsPane({
  header,
  metrics,
  bodyVariant,
  trialMinutes,
  marketAddr,
  account,
  activeTrial,
  lockCountdown,
  claim,
  settle,
  positionFlat,
  settleStalled,
}: DetailsPaneProps) {
  const showChart = bodyVariant === "active-trial" || bodyVariant === "settled-trial";
  const chartMarket = activeTrial?.market ?? marketAddr ?? "";

  return (
    <div className={containerStyles} data-testid="fft-details-pane" data-variant={bodyVariant}>
      <DetailsHeader {...header} />
      <DetailsMetricsRow metrics={metrics} />
      <Body
        variant={bodyVariant}
        trialMinutes={trialMinutes}
        account={account}
        activeTrial={activeTrial}
        lockCountdown={lockCountdown}
        metrics={metrics}
        claim={claim}
        settle={settle}
        positionFlat={positionFlat}
        settleStalled={settleStalled}
      />
      {showChart && chartMarket && (
        <div className={flex({ px: "16", pb: "16", flex: "1", align: "stretch" })}>
          <FftTrialChartDynamic
            trial={activeTrial}
            marketAddr={chartMarket}
            positionFlat={positionFlat}
          />
        </div>
      )}
      {bodyVariant === "active-trial" && <TrialFootnote trialMinutes={trialMinutes} />}
      {showChart && metrics && (
        <div className={mtAuto}>
          <FundsUnlockFooter
            lockedLabel={metrics.locked}
            unlocksAtLabel={metrics.unlocksAt}
            claim={claim}
          />
        </div>
      )}
    </div>
  );
}

interface BodyProps {
  variant: DetailsBodyVariant;
  trialMinutes: number;
  account?: string;
  activeTrial?: TrialDto;
  lockCountdown?: string | null;
  metrics: DetailsMetricsRowProps["metrics"];
  claim?: ClaimState;
  settle?: SettleAction;
  positionFlat?: boolean;
  settleStalled?: boolean;
}

const containerStyles = stack({
  gap: "16",
  flex: "1",
  w: "full",
  bg: "card.background",
  borderWidth: "100",
  borderStyle: "solid",
  borderColor: "card.border",
  rounded: "200",
});

function Body({
  variant,
  trialMinutes,
  account,
  activeTrial,
  lockCountdown,
  metrics,
  claim,
  settle,
  positionFlat,
  settleStalled,
}: BodyProps) {
  switch (variant) {
    case "pre-lock":
      return (
        <>
          <TrialHistorySection account={account} />
          <DetailsHero trialMinutes={trialMinutes} />
        </>
      );
    case "post-lock-empty":
      return (
        <>
          <TrialHistorySection
            account={account}
            emptyFallback={<DetailsEmptyTrades lockCountdown={lockCountdown} />}
            lockCountdown={lockCountdown}
          />
          {metrics && (
            <div className={mtAuto}>
              <FundsUnlockFooter
                lockedLabel={metrics.locked}
                unlocksAtLabel={metrics.unlocksAt}
                claim={claim}
              />
            </div>
          )}
        </>
      );
    case "active-trial":
      return activeTrial ? (
        <TrialPositionsTable
          trial={activeTrial}
          settle={settle}
          positionFlat={positionFlat}
          settleStalled={settleStalled}
        />
      ) : (
        <TrialPositionsTableSkeleton />
      );
    case "settled-trial":
      return activeTrial ? <SettleScreen trial={activeTrial} /> : null;
  }
}

const mtAuto = css({ mt: "auto" });
